import React, { useCallback, useState } from "react";
import { filter, size as length, map, toUpper } from "lodash";
import { HexColorInput, HexColorPicker } from "react-colorful";
import styled, { css } from "styled-components";
import {
  Color,
  Elevation,
  FontSize,
  FontWeight,
  Radius,
  Spacing,
  border,
  content,
  dominant,
  form,
  parseToRgb,
  rgbToHex,
  size,
  transparentize,
} from "@glossy";
import { Locale } from "@linkfluence/models/locale";
import Tooltip from "@ui-kit/tooltip";
import { tr } from "@radarly/i18n";

interface IProps {
  currentColor: string;
  onChange: (hex: string) => void;
  presets: { title: string; colors: { name?: string; hex: string }[] }[];
  locale: Locale;
}

const Root = styled.div`
  .react-colorful {
    width: auto;
    height: auto;
  }

  .react-colorful__saturation {
    border-radius: ${Radius.Medium};
    border: 1px solid ${border};
    height: 124px;
    flex-grow: 0;
  }

  .react-colorful__hue {
    margin: ${Spacing.Medium} 0 ${Spacing.Large};
    border-radius: ${Radius.Small};
    height: 12px;
  }

  .react-colorful__pointer {
    height: 16px;
    width: 16px;
  }

  .react-colorful__saturation-pointer {
    box-shadow: ${Elevation.Elevation_1};
  }
`;

const RgbInputs = styled.div`
  display: flex;
  column-gap: ${Spacing.ExtraSmall};

  > * {
    flex: 1;
    text-align: center;
  }
`;

const inputs = css`
  padding: 0;
  color: ${form};
  font-weight: ${FontWeight.Normal};
  font-size: ${FontSize.Medium};
  width: 100%;
  box-sizing: border-box;
  background-color: ${(props) => transparentize(0.85, form(props, "primary"))};

  :hover {
    background-color: ${(props) => transparentize(0.8, form(props, "primary"))};
  }

  :focus {
    box-shadow: 0 0 0 2px ${dominant} inset;
    background-color: transparent;
  }
`;

const PreviewAndHex = styled.div`
  display: flex;
  column-gap: ${Spacing.ExtraSmall};
  margin-bottom: ${Spacing.Medium};
`;

const Preview = styled.div`
  ${size(36)};
  border-radius: ${Radius.Medium};
  flex-shrink: 0;
`;

const Hex = styled.div`
  color: ${form};
  opacity: 0.7;
  line-height: 36px;
  flex-grow: 1;
  text-align: right;
  font-size: ${FontSize.Small};
  padding: 0 ${Spacing.Medium};
`;

const HexInputWrapper = styled.div`
  position: relative;
  flex-basis: calc(
    (100% - 2 * ${Spacing.ExtraSmall}) * 2 / 3 + ${Spacing.ExtraSmall}
  );

  &:after {
    content: "#";
    position: absolute;
    left: 16px;
    top: 0;
    line-height: 36px;
    color: ${form};
    opacity: 0.7;
  }
`;

const StyledHexColorInput = styled(HexColorInput)`
  ${inputs};
  border-radius: ${Radius.Medium};
  line-height: 36px;
  padding-left: 32px;
`;

const RgbInput = styled.input`
  ${inputs};
  border-radius: ${Radius.Small};
  line-height: 24px;
  text-align: center;
  padding-left: 16px;
`;

const Letter = styled.div`
  font-size: ${FontSize.Small};
  color: ${(props) => content(props, "secondary")};
  line-height: 30px;
`;

const Presets = styled.div`
  margin-top: ${Spacing.Medium};
  padding-top: ${Spacing.Medium};
  border-top: 1px solid ${(props) => content(props, "tertiary")};
`;

const PresetGroup = styled.div`
  &:not(:last-child) {
    margin-bottom: ${Spacing.Medium};
  }
`;

const PresetTitle = styled.div`
  margin-bottom: ${Spacing.Medium};
  color: ${form};
  font-weight: ${FontWeight.Normal};
  font-size: ${FontSize.Medium};
`;

const Swatches = styled.div`
  display: flex;
  flex-wrap: wrap;
  margin: -3px;
  gap: ${Spacing.ExtraSmall};
`;

const Swatch = styled.div<{ selected: boolean }>`
  ${size(24)};
  box-sizing: content-box;
  border-radius: 7px;
  cursor: pointer;
  background-clip: content-box;
  padding: 1px;
  border: 2px solid
    ${(props) => (props.selected ? dominant(props) : "transparent")};

  :hover {
    transform: scale(${28 / 24});
  }
`;

const useColor = (currentColor, onChange) => {
  const [hex, setHex] = useState(toUpper(currentColor));
  const [rgb, setRgb] = useState(parseToRgb(hex));

  const onHexChange = useCallback(
    (hex) => {
      setHex(toUpper(hex));
      setRgb(parseToRgb(hex));
      onChange(toUpper(hex));
    },
    [onChange]
  );

  const onRgbChange = useCallback(
    (value) => (e) => {
      const newRgb = {
        ...rgb,
        [value]: Math.min(Math.max(parseInt(e.target.value) || 0, 0), 255),
      };
      setRgb(newRgb);
      const newHex = toUpper(rgbToHex(newRgb.red, newRgb.green, newRgb.blue));
      setHex(newHex);
      onChange(newHex);
    },
    [onChange, rgb]
  );

  return { hex, rgb, onHexChange, onRgbChange };
};

const UiKitColorPicker = ({
  currentColor = Color.LightSeaGreen,
  onChange,
  presets,
  locale,
}: IProps) => {
  const { hex, rgb, onHexChange, onRgbChange } = useColor(
    currentColor,
    onChange
  );

  const filteredPresets = filter(presets, (p) => length(p.colors) > 0);
  const hasPresets = length(filteredPresets);

  return (
    <Root>
      <HexColorPicker color={hex} onChange={onHexChange} />
      <PreviewAndHex>
        <Preview style={{ backgroundColor: hex }} />
        <Hex>{tr(locale, "COLOR_PICKER::HEX")}</Hex>
        <HexInputWrapper>
          <StyledHexColorInput color={hex} onChange={onHexChange} prefixed />
        </HexInputWrapper>
      </PreviewAndHex>
      <RgbInputs>
        {map(["red", "green", "blue"], (primary, i) => (
          <div key={i}>
            <RgbInput
              key={primary}
              type="number"
              step="1"
              min="0"
              max="255"
              value={rgb[primary]}
              onChange={onRgbChange(primary)}
            />
            <Letter>{toUpper(primary[0])}</Letter>
          </div>
        ))}
      </RgbInputs>

      {hasPresets && (
        <Presets>
          {map(filteredPresets, (presetGroup, i) => (
            <PresetGroup key={i}>
              <PresetTitle>{presetGroup.title}</PresetTitle>
              <Swatches>
                {map(presetGroup.colors, (presetColor, j) => (
                  <Tooltip
                    tooltip={presetColor.name || presetColor.hex}
                    tiny
                    key={j}
                  >
                    <Swatch
                      style={{ backgroundColor: presetColor.hex }}
                      onClick={() => onHexChange(presetColor.hex)}
                      selected={toUpper(presetColor.hex) === hex}
                    />
                  </Tooltip>
                ))}
              </Swatches>
            </PresetGroup>
          ))}
        </Presets>
      )}
    </Root>
  );
};

export default UiKitColorPicker;
